import { useDraggable } from '@dnd-kit/core'
import type { AddToProductImage } from '../types'

interface SourceImageCardProps {
  image: AddToProductImage
}

/**
 * A single draggable source image. Carries `{ image }` in its drag data so the
 * central drop handler (makeDropHandler → resolveDrop) can resolve it.
 *
 * Visual: the source element stays in place; the floating cursor preview is
 * rendered by `DragOverlay` in AddToProductWorkspace. Applying `transform`
 * here would get clipped by the source panel's `overflow-hidden`, so we only
 * fade the original to signal "picked up".
 */
export function SourceImageCard({ image }: SourceImageCardProps) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `src-${image.id}`,
    data: { image },
  })
  const style: React.CSSProperties = {
    opacity: isDragging ? 0.35 : 1,
    touchAction: 'none',
  }
  return (
    <button
      ref={setNodeRef}
      type="button"
      style={style}
      data-testid={`atp-srcimg-${image.id}`}
      className="aspect-square rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 cursor-grab active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <img src={image.url} alt="" className="w-full h-full object-cover pointer-events-none" />
    </button>
  )
}
